// frontend/src/components/leads/ViewLead.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LeadStatusBadge from './LeadStatusBadge';
import ActivityLog from '../activities/ActivityLog';
import AddActivity from '../activities/AddActivity';
import DocumentUpload from '../documents/DocumentUpload';
import DocumentList from '../documents/DocumentList';
import { formatInUserTimezone, formatUtcDateTimeParts } from '../../utils/timezone';
import '../../styles/LeadPanel.css';

function ViewLead({ leadId: embeddedLeadId, embedded = false, onClose, onChanged }) {
  const { id: routeLeadId } = useParams();
  const navigate = useNavigate();
  const id = embeddedLeadId || routeLeadId;

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);

  const [showActivity, setShowActivity] = useState(false);
  const [addingActivity, setAddingActivity] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newLeadStatus, setNewLeadStatus] = useState('');
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);

  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');

  const fetchLead = useCallback(async () => {
    if (!id) {
      setError('Lead ID is missing');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.getLead(id);
      if (!data) {
        throw new Error('Lead not found');
      }
      setLead(data);
      setNotes(data.notes || '');
      setNewLeadStatus(data.status || 'New');
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Failed to load lead details:', err);
      setLead(null);
      setError(err?.response?.data?.error || 'Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  useEffect(() => {
    api.getMasterData('leadStatuses')
      .then(values => {
        setLeadStatusOptions(Array.isArray(values) ? values : []);
      })
      .catch(error => console.error('Failed to load lead statuses:', error));
  }, []);

  const refreshAfterChange = async () => {
    await fetchLead();
    if (onChanged) {
      await onChanged();
    }
  };

  const handleAddActivity = async (activityData) => {
    if (!activityData?.type) {
      alert('Activity type is required');
      return;
    }
    if (!activityData?.description?.trim()) {
      alert('Activity description is required');
      return;
    }

    setAddingActivity(true);
    try {
      const payload = {
        leadId: Number(id),
        type: activityData.type,
        description: activityData.description.trim(),
        scheduledDate: activityData.scheduledDate || new Date().toISOString()
      };
      await api.addActivity(payload);
      setShowActivity(false);
      await refreshAfterChange();
    } catch (err) {
      console.error('Failed to add activity:', err);
      alert(err?.response?.data?.error || 'Failed to add activity');
    } finally {
      setAddingActivity(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      const updatedLead = await api.updateLead(id, { notes });
      if (updatedLead) {
        setLead(prev => ({ ...prev, ...updatedLead, notes }));
      } else {
        setLead(prev => ({ ...prev, notes }));
      }
      setEditingNotes(false);
      await refreshAfterChange();
    } catch (err) {
      console.error('Failed to save notes:', err);
      alert(err?.response?.data?.error || 'Failed to save notes');
    }
  };

  const handleLeadStatusUpdate = async () => {
    if (!newLeadStatus) return;
    try {
      await api.updateLead(id, { status: newLeadStatus });
      await refreshAfterChange();
      setShowStatusUpdate(false);
    } catch (err) {
      console.error('Failed to update lead status:', err);
      alert(err?.response?.data?.error || 'Failed to update lead status');
    }
  };

  const handleEdit = () => navigate(`/leads/${id}/edit`);
  const handleBookDemo = () => navigate(`/leads/${id}/demo`);

  const handleClose = () => {
    if (embedded && onClose) {
      onClose();
      return;
    }
    navigate('/leads');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return formatInUserTimezone(date);
    } catch {
      return date;
    }
  };

  if (loading) {
    return !embedded ? (
      <div className="lead-panel-page-state">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading lead details...</p>
        </div>
      </div>
    ) : (
      <div className="lead-panel-overlay" onMouseDown={handleClose}>
        <aside className="lead-panel" onMouseDown={e => e.stopPropagation()} aria-label="Lead details">
          <div className="lead-panel-loading">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading lead details...</p>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  if (error || !lead) {
    return !embedded ? (
      <div className="lead-panel-page-state">
        <div className="error-container">
          <div className="error-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <div className="error-message">{error || 'Lead not found'}</div>
          <button className="btn btn-secondary" onClick={handleClose}>
            Back to Leads
          </button>
        </div>
      </div>
    ) : (
      <div className="lead-panel-overlay" onMouseDown={handleClose}>
        <aside className="lead-panel" onMouseDown={e => e.stopPropagation()} aria-label="Lead details">
          <div className="lead-panel-error">
            <button className="lead-panel-close" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
            <div className="error-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-message">{error || 'Lead not found'}</div>
            <button className="btn btn-secondary" onClick={fetchLead}>
              Retry
            </button>
          </div>
        </aside>
      </div>
    );
  }

  const demoStatus = lead.demo_status || lead.demoStatus || 'Not Booked';
  const demoDate = lead.demo_date || lead.demoDate || '';
  const demoTime = lead.demo_time || lead.demoTime || '';
  const demoDateTime = demoDate || demoTime ? formatUtcDateTimeParts(demoDate, demoTime) : 'N/A';

  const dmsProviders = Array.isArray(lead.dms_providers)
    ? lead.dms_providers
    : Array.isArray(lead.dmsProviders)
      ? lead.dmsProviders
      : [];

  const manufacturers = Array.isArray(lead.manufacturer) ? lead.manufacturer : [];
  const products = Array.isArray(lead.products) ? lead.products : [];
  const contacts = Array.isArray(lead.contacts) ? lead.contacts : [];
  const activities = Array.isArray(lead.activities) ? lead.activities : [];

  const panelContent = (
    <>
      <div className="lead-panel-top">
        <div>
          <div className="lead-panel-eyebrow">Lead Details</div>
          <h2>{lead.company || 'Lead'}</h2>
          <div className="lead-panel-subtitle">Lead #{lead.lead_id || id}</div>
        </div>
        <button type="button" className="lead-panel-close" onClick={handleClose} aria-label="Close lead details">
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="lead-panel-actions">
        <button type="button" className="btn btn-primary" onClick={handleEdit}>
          <i className="fas fa-edit"></i> Edit Lead
        </button>
        {demoStatus === 'Not Booked' && (
          <button type="button" className="btn btn-success" onClick={handleBookDemo}>
            <i className="fas fa-calendar-plus"></i> Book Demo
          </button>
        )}
      </div>

      <section className="lead-panel-section">
        <div className="lead-panel-section-title">Dealership</div>
        <div className="lead-panel-grid">
          <Detail label="Dealership Name" value={lead.company} />
          <Detail label="DMS Providers" value={dmsProviders.length ? dmsProviders.join(', ') : 'N/A'} />
          <Detail label="Other DMS Provider" value={lead.other_dms_provider || lead.otherDmsProvider} />
          <Detail label="Dealer Group" value={lead.dealer_group || lead.dealerGroup} />
          <Detail label="Manufacturer" value={manufacturers.length ? manufacturers.join(', ') : 'N/A'} />
          <Detail label="Rooftops" value={lead.rooftops !== null && lead.rooftops !== undefined ? lead.rooftops : 'N/A'} />
          <Detail label="Dealer Main Line" value={lead.main_line || lead.mainLine} />
          <Detail label="Dealership Email" value={lead.email} />
          <Detail label="Website" value={lead.website} />
          <Detail label="Street Address" value={lead.street_address || lead.streetAddress} />
          <Detail label="City" value={lead.city} />
          <Detail label="State" value={lead.state} />
          <Detail label="ZIP" value={lead.zip_code || lead.zipCode} />
          <Detail label="Country" value={lead.country} />
          <Detail label="Sales Agent / Owner" value={lead.partner_name || lead.salesRepName} />
        </div>
      </section>

      <section className="lead-panel-section">
        <div className="lead-panel-section-header">
          <div className="lead-panel-section-title">Lead Information</div>
          {!showStatusUpdate ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setNewLeadStatus(lead.status || 'New');
                setShowStatusUpdate(true);
              }}
            >
              <i className="fas fa-edit"></i> Update Status
            </button>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowStatusUpdate(false)}>
              <i className="fas fa-times"></i> Cancel
            </button>
          )}
        </div>
        {showStatusUpdate && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              className="form-control"
              value={newLeadStatus}
              onChange={e => setNewLeadStatus(e.target.value)}
              style={{ flex: 1 }}
            >
              {leadStatusOptions.map(option => (
                <option key={option.id || option.name} value={option.name}>
                  {option.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary" onClick={handleLeadStatusUpdate}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
        )}
        <div className="lead-panel-grid" style={{ marginTop: '12px' }}>
          <Detail label="Lead Status" value={<LeadStatusBadge status={lead.status} />} />
          <Detail label="Lead Type" value={lead.lead_type || lead.leadType || 'New'} />
          <Detail label="Lead Source" value={lead.lead_source || lead.leadSource} />
          <Detail label="Choose Service Dashboard" value={lead.service_dashboard || lead.serviceDashboard || 'None'} />
          <Detail label="Contact Date" value={formatDate(lead.contact_date || lead.contactDate)} />
          <Detail label="Opportunity" value={lead.opportunity_yn || lead.opportunityYn || 'Not Set'} />
          <Detail label="Next Steps" value={lead.next_steps || lead.nextSteps} />
          <Detail label="Created By" value={lead.created_by || lead.createdBy} />
          <Detail label="Created At" value={formatDate(lead.created_at)} />
          <Detail label="Last Updated" value={formatDate(lead.updated_at)} />
        </div>
      </section>

      <section className="lead-panel-section">
        <div className="lead-panel-section-title">Contacts</div>
        {contacts.length > 0 ? (
          <div className="lead-panel-contacts">
            {contacts.map((contact, index) => (
              <div className="lead-contact-card" key={contact.contact_id || contact.id || index}>
                <div className="lead-contact-name">
                  {[contact.first_name || contact.firstName, contact.last_name || contact.lastName]
                    .filter(Boolean)
                    .join(' ') || 'N/A'}
                </div>
                <div className="lead-contact-meta">{contact.title || 'N/A'}</div>
                <div className="lead-contact-lines">
                  <span>
                    <i className="fas fa-phone"></i> Work: {contact.work_phone || contact.workPhone || 'N/A'}
                  </span>
                  <span>
                    <i className="fas fa-phone-alt"></i> Home: {contact.home_phone || contact.homePhone || 'N/A'}
                  </span>
                  <span>
                    <i className="fas fa-mobile-alt"></i> Mobile: {contact.mobile_phone || contact.mobilePhone || 'N/A'}
                  </span>
                  <span>
                    <i className="fas fa-envelope"></i> {contact.email || 'N/A'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="lead-panel-empty">No contacts available</div>
        )}
      </section>

      <section className="lead-panel-section">
        <div className="lead-panel-section-title">Products</div>
        {products.length > 0 ? (
          <div className="lead-panel-tags">
            {products.map((product, index) => (
              <span className="lead-panel-tag" key={`${product}-${index}`}>
                {product}
              </span>
            ))}
          </div>
        ) : (
          <div className="lead-panel-empty">No products selected</div>
        )}
      </section>

      <section className="lead-panel-section">
        <div className="lead-panel-section-title">Demo Information</div>
        <div className="lead-panel-grid">
          <Detail
            label="Status"
            value={
              <span className={`demo-status-${String(demoStatus).toLowerCase().replace(/\s+/g, '-')}`}>
                {demoStatus}
              </span>
            }
          />
          <Detail label="Date & Time" value={demoDateTime} />
          {lead.demo_timezone && <Detail label="Timezone" value={lead.demo_timezone} />}
          {lead.microsoft_booking_id && <Detail label="Microsoft Booking ID" value={lead.microsoft_booking_id} />}
        </div>
        {demoStatus === 'Not Booked' && (
          <button type="button" className="btn btn-success" onClick={handleBookDemo} style={{ marginTop: '12px' }}>
            <i className="fas fa-calendar-plus"></i> Book Demo
          </button>
        )}
      </section>

      <section className="lead-panel-section">
        <div className="lead-panel-section-header">
          <div className="lead-panel-section-title">
            <i className="fas fa-paperclip" style={{ marginRight: '8px', color: '#48A0DC' }}></i>
            Documents
            <span className="badge badge-secondary" style={{ marginLeft: '8px' }}>
              {documents.length}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowDocumentUpload(!showDocumentUpload)}
          >
            <i className={`fas fa-${showDocumentUpload ? 'times' : 'plus'}`}></i>
            {showDocumentUpload ? 'Close' : 'Add Documents'}
          </button>
        </div>

        {showDocumentUpload && (
          <DocumentUpload
            leadId={id}
            onUploadComplete={async newDocs => {
              setDocuments(prev => [...prev, ...newDocs]);
              setShowDocumentUpload(false);
              await refreshAfterChange();
            }}
          />
        )}

        <DocumentList
          documents={documents}
          leadId={id}
          onDelete={docId => {
            setDocuments(prev => prev.filter(d => d.document_id !== docId));
            refreshAfterChange();
          }}
          onChanged={async () => {
            await refreshAfterChange();
          }}
        />
      </section>

      <section className="lead-panel-section">
        <div className="lead-panel-section-header">
          <div className="lead-panel-section-title">Activity</div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowActivity(prev => !prev)}
            disabled={addingActivity}
          >
            <i className={`fas ${showActivity ? 'fa-times' : 'fa-plus'}`}></i>
            {showActivity ? 'Close' : 'Log Activity'}
          </button>
        </div>

        {showActivity && (
          <AddActivity
            onSave={handleAddActivity}
            onCancel={() => setShowActivity(false)}
            saving={addingActivity}
          />
        )}

        <ActivityLog activities={activities} />
      </section>

      <section className="lead-panel-section">
        <div className="lead-panel-section-header">
          <div className="lead-panel-section-title">Notes</div>
          {!editingNotes && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setEditingNotes(true)}
            >
              <i className="fas fa-edit"></i>
              {notes ? 'Edit Note' : 'Add Note'}
            </button>
          )}
        </div>

        {editingNotes ? (
          <>
            <textarea
              className="form-control lead-panel-notes-editor"
              rows="6"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Enter notes about this lead..."
            />
            <div className="lead-panel-note-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setNotes(lead.notes || '');
                  setEditingNotes(false);
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveNotes}>
                <i className="fas fa-save"></i> Save Note
              </button>
            </div>
          </>
        ) : (
          <div className="lead-panel-notes">
            {notes ? notes : <span className="text-muted">No notes recorded</span>}
          </div>
        )}
      </section>
    </>
  );

  if (!embedded) {
    return <div className="lead-panel-page-state">{panelContent}</div>;
  }

  return (
    <div className="lead-panel-overlay" onMouseDown={handleClose}>
      <aside
        className="lead-panel"
        onMouseDown={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Lead details for ${lead.company || 'lead'}`}
      >
        {panelContent}
      </aside>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="lead-panel-detail">
      <span className="lead-panel-detail-label">{label}</span>
      <span className="lead-panel-detail-value">{value || 'N/A'}</span>
    </div>
  );
}

export default ViewLead;